import React, { useEffect, useRef, useState } from 'react';
import { Modal } from './ui';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (currentPassword: string, newPassword: string) => Promise<void>;
}

const TITLE_ID = 'change-password-title';
const MIN_LEN = 8;

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError(null);
      setSubmitting(false);
      requestAnimationFrame(() => currentRef.current?.focus());
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Fill in every field.');
      return;
    }
    if (newPassword.length < MIN_LEN) {
      setError(`New password must be at least ${MIN_LEN} characters.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit(currentPassword, newPassword);
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? 'Could not update password.');
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isBusy={submitting} labelledBy={TITLE_ID}>
      <form onSubmit={handleSubmit} noValidate>
        <Modal.Header
          title="Change password"
          titleId={TITLE_ID}
          subtitle={`At least ${MIN_LEN} characters. You'll stay signed in on this device.`}
        />

        <Modal.Body>
          <label className="pb-modal-field pt-4">
            <span className="pb-modal-label">Current password</span>
            <input
              ref={currentRef}
              type="password"
              className="pb-modal-input"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              disabled={submitting}
            />
          </label>

          <label className="pb-modal-field">
            <span className="pb-modal-label">New password</span>
            <input
              type="password"
              className="pb-modal-input"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              disabled={submitting}
            />
          </label>

          <label className="pb-modal-field">
            <span className="pb-modal-label">Confirm new password</span>
            <input
              type="password"
              className="pb-modal-input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              disabled={submitting}
            />
          </label>

          {error && <p className="pb-modal-error" role="alert">{error}</p>}
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="pb-modal-btn pb-modal-btn--ghost"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="pb-modal-btn pb-modal-btn--primary"
            disabled={submitting || !currentPassword || !newPassword || !confirmPassword}
          >
            {submitting ? 'Saving…' : 'Update password'}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
